import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import {
  messages,
} from 'constantes';
import {
  ReportableException,
  UnprocessableException,
} from '../exceptions';

/**
 * Higher-Order Component that injects notification helper functions using notistack.
 *
 * @param {React.ComponentType} WrappedComponent - Component to wrap with notification helpers
 * @return {React.ComponentType} Wrapped component with notification props
 */
const withNotification = (WrappedComponent) => {
  const NotificationWrapper = (props) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const onClose = (event, reason, key) => {
      if (reason === 'clickaway') {
        closeSnackbar(key);
      }
    };
    const appInfo = (message, options = {}) => enqueueSnackbar(message, {
      variant: 'info',
      onClose: (event, reason, key) => onClose(event, reason, key),
      ...options,
    });
    const appSuccess = (message, options = {}) => enqueueSnackbar(message, {
      variant: 'success',
      onClose: (event, reason, key) => onClose(event, reason, key),
      ...options,
    });
    const appWarning = (message, options = {}) => enqueueSnackbar(message, {
      variant: 'warning',
      onClose: (event, reason, key) => onClose(event, reason, key),
      ...options,
    });
    const appError = (message) => {
      enqueueSnackbar(message, {
        variant: 'error',
        onClose: (event, reason, key) => onClose(event, reason, key),
      });
    };
    const appNotify = (message, options = {}) => enqueueSnackbar(message, {
      onClose: (event, reason, key) => onClose(event, reason, key),
      ...options,
    });

    const genericException = (exception) => {
      if (exception instanceof ReportableException) {
        appWarning(exception.message);
      } else if (exception instanceof UnprocessableException) {
        appWarning(exception.message);
      } else {
        appError(messages.dataFetch.fail, exception);
      }
    };
    return (
      <WrappedComponent
        appInfo={appInfo}
        appWarning={appWarning}
        appSuccess={appSuccess}
        genericException={genericException}
        appError={appError}
        appNotify={appNotify}
        {...props}
      />
    );
  };
  const componentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  NotificationWrapper.displayName = `withNotification(${componentName})`;
  return NotificationWrapper;
};

export const propTypes = {
  appInfo: PropTypes.func.isRequired,
  appSuccess: PropTypes.func.isRequired,
  appWarning: PropTypes.func.isRequired,
  appError: PropTypes.func.isRequired,
  appNotify: PropTypes.func.isRequired,
};

export default withNotification;
